import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';

import ShopByHeaderSection from 'components/consumer/ShopByHeaderSection';
import ToggleButtonGroup from 'components/consumer/ToggleButtonGroup';
import {
  STYLE_OPTIONS,
  LEG_STYLE_OPTIONS,
  CUSHION_STYLE_OPTIONS,
} from 'commons/constants';
import { GET_COLLECTIONS } from 'mocks/queries';
import ShopByTitleNavigation from 'components/consumer/ShopByTitleNavigation';
import ExpandableModalOptions from 'components/consumer/ExpandableModalOptions';
import ShopByMobileFiltersBar from 'components/consumer/ShopByMobileFiltersBar';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import { COLLECTIONS_PAGE_SEO_DESCRIPTION } from '../../../commons/constants';
import CollectionsGrid from './components/CollectionsGrid';
import { withState } from './withState';
import ShopByMobileFilterBarButton from './components/ShopByMobileFiltersBarButton';
import MobileOnly from '../MobileOnly';
import TabletAndDesktopOnly from '../TabletAndDesktopOnly';

const CollectionPage = ({ state, actions, LUXContext = null }) => {
  const [isMobileStyleFiltersOpen, setIsMobileStyleFiltersOpen] = useState(
    false
  );

  const [isMobileCushionFiltersOpen, setIsMobileCushionFiltersOpen] = useState(
    false
  );

  const [
    isMobileLegStyleFiltersOpen,
    setIsMobileLegStyleFiltersOpen,
  ] = useState(false);

  const [LUXLoadedSent, updateLUXLoaded] = useState(false);

  const { data, error, loading } = useSuspenseQuery(GET_COLLECTIONS, {
    variables: { filters: state.filters },
    fetchPolicy: 'cache-and-network',
  });

  const sendLUXLoaded = () => {
    if (!LUXLoadedSent && LUXContext && LUXContext.isLUXInitSet) {
      LUXContext.updatedLoadedComponents('CollectionsPage');
      updateLUXLoaded(true);
    }
  };

  const AllFilters = useCallback(
    () => (
      <>
        <ShopByHeaderSection label="Style">
          <ToggleButtonGroup
            items={[
              {
                label: 'All',
                id: 'all',
                isSelected: typeof state.filters.style === 'undefined',
                onClick: () => {
                  actions.clearFilterField('style');
                },
              },
              ...STYLE_OPTIONS.map(option => {
                const isSelected =
                  state.filters.style && state.filters.style[option.value];

                return {
                  label: option.label,
                  id: option.value,
                  isSelected,
                  onClick: () => {
                    actions.onFilterChange('style', option.value);
                  },
                };
              }),
            ]}
          />
        </ShopByHeaderSection>

        <ShopByHeaderSection label="Cushion">
          <ToggleButtonGroup
            items={[
              {
                label: 'Any',
                id: 'any',
                isSelected: typeof state.filters.cushion_style === 'undefined',
                onClick: () => {
                  actions.clearFilterField('cushion_style');
                },
              },
              ...CUSHION_STYLE_OPTIONS.map(option => {
                const isSelected =
                  state.filters.cushion_style &&
                  state.filters.cushion_style[option.value];

                return {
                  label: option.label,
                  id: option.value,
                  isSelected,
                  onClick: () => {
                    actions.onFilterChange('cushion_style', option.value);
                  },
                };
              }),
            ]}
          />
        </ShopByHeaderSection>

        <ShopByHeaderSection label="Leg Style">
          <ToggleButtonGroup
            items={[
              {
                label: 'Any',
                id: 'any',
                isSelected: typeof state.filters.leg_style === 'undefined',
                onClick: () => {
                  actions.clearFilterField('leg_style');
                },
              },
              ...LEG_STYLE_OPTIONS.map(option => {
                const isSelected =
                  state.filters.leg_style &&
                  state.filters.leg_style[option.value];

                return {
                  label: option.label,
                  id: option.value,
                  isSelected,
                  onClick: () => {
                    actions.onFilterChange('leg_style', option.value);
                  },
                };
              }),
            ]}
          />
        </ShopByHeaderSection>
      </>
    ),
    [
      actions,
      state.filters.cushion_style,
      state.filters.leg_style,
      state.filters.style,
    ]
  );

  let filtersConfig = {};
  let finalCollections = [];

  if (
    !error &&
    !loading &&
    data.getCollections &&
    data.getCollections.collections.length
  ) {
    if (!LUXLoadedSent) {
      sendLUXLoaded();
    }

    filtersConfig = data.getCollections.filtersConfig;
    finalCollections = actions.addMainImages(data.getCollections.collections);
  }

  return (
    <>
      <Helmet>
        <title>Collection Pages | Joybird</title>
        <meta name="keywords" content="Collection Page" />
        <meta name="description" content={COLLECTIONS_PAGE_SEO_DESCRIPTION} />
        <meta property="og:title" content="Collection Pages | Joybird" />

        <meta
          property="og:description"
          content={COLLECTIONS_PAGE_SEO_DESCRIPTION}
        />

        <meta property="og:type" content="website" />
      </Helmet>

      <div className="flex relative flex-col lg:block">
        <ShopByTitleNavigation page="Collection" />

        {!error && (
          <>
            <div className="flex border-b-[1px] border-b-gray-light7 flex-wrap md:pt-4 md:px-[3.333vw] px-12">
              <MobileOnly>
                <ShopByMobileFiltersBar variant="collections-page">
                  <ShopByMobileFilterBarButton
                    isOpen={isMobileStyleFiltersOpen}
                    onClick={() => {
                      setIsMobileStyleFiltersOpen(!isMobileStyleFiltersOpen);
                    }}
                  >
                    <span className="h-full">
                      {typeof state.filters.style === 'undefined'
                        ? 'All Styles'
                        : STYLE_OPTIONS.find(
                            option =>
                              !!state.filters.style &&
                              !!state.filters.style[option.value]
                          ).label}
                    </span>
                  </ShopByMobileFilterBarButton>

                  <ShopByMobileFilterBarButton
                    isOpen={isMobileCushionFiltersOpen}
                    onClick={() => {
                      setIsMobileCushionFiltersOpen(
                        !isMobileCushionFiltersOpen
                      );
                    }}
                  >
                    <span className="h-full">
                      {typeof state.filters.cushion_style === 'undefined'
                        ? 'Cushion'
                        : CUSHION_STYLE_OPTIONS.find(
                            option =>
                              !!state.filters.cushion_style &&
                              !!state.filters.cushion_style[option.value]
                          ).label}
                    </span>
                  </ShopByMobileFilterBarButton>

                  <ShopByMobileFilterBarButton
                    isOpen={isMobileLegStyleFiltersOpen}
                    onClick={() => {
                      setIsMobileLegStyleFiltersOpen(
                        !isMobileLegStyleFiltersOpen
                      );
                    }}
                  >
                    <span className="h-full">
                      {typeof state.filters.leg_style === 'undefined'
                        ? 'Leg Style'
                        : LEG_STYLE_OPTIONS.find(
                            option =>
                              !!state.filters.leg_style &&
                              !!state.filters.leg_style[option.value]
                          ).label}
                    </span>
                  </ShopByMobileFilterBarButton>
                </ShopByMobileFiltersBar>
              </MobileOnly>

              <TabletAndDesktopOnly>
                <div className="max-[767px]:hidden flex flex-wrap">
                  <AllFilters loading={loading} filtersConfig={filtersConfig} />
                </div>
              </TabletAndDesktopOnly>
            </div>

            <div className="relative">
              <CollectionsGrid
                loading={loading}
                collections={finalCollections}
                selectedCollection={state.selectedCollection}
                clearFilters={async () => {
                  await actions.clearFilterField('style');
                  await actions.clearFilterField('cushion_style');
                  await actions.clearFilterField('leg_style');
                }}
                filters={state.filters}
              />

              <MobileOnly>
                <ExpandableModalOptions
                  isOpen={isMobileStyleFiltersOpen}
                  onClose={() => {
                    setIsMobileStyleFiltersOpen(false);
                  }}
                  label="Styles"
                  options={[
                    {
                      label: 'All',
                      id: 'all',
                      isSelected: typeof state.filters.style === 'undefined',
                      onClick: () => {
                        actions.clearFilterField('style');
                        setIsMobileStyleFiltersOpen(false);
                      },
                    },
                    ...STYLE_OPTIONS.map(option => {
                      const isSelected =
                        state.filters.style &&
                        state.filters.style[option.value];

                      return {
                        label: option.label,
                        id: option.value,
                        isSelected,
                        onClick: () => {
                          actions.onFilterChange('style', option.value);
                          setIsMobileStyleFiltersOpen(false);
                        },
                      };
                    }),
                  ]}
                />

                <ExpandableModalOptions
                  isOpen={isMobileCushionFiltersOpen}
                  onClose={() => {
                    setIsMobileCushionFiltersOpen(false);
                  }}
                  label="Cushion"
                  options={[
                    {
                      label: 'Any',
                      id: 'any',
                      isSelected:
                        typeof state.filters.cushion_style === 'undefined',
                      onClick: () => {
                        actions.clearFilterField('cushion_style');
                        setIsMobileCushionFiltersOpen(false);
                      },
                    },
                    ...CUSHION_STYLE_OPTIONS.map(option => {
                      const isSelected =
                        state.filters.cushion_style &&
                        state.filters.cushion_style[option.value];

                      return {
                        label: option.label,
                        id: option.value,
                        isSelected,
                        onClick: () => {
                          actions.onFilterChange('cushion_style', option.value);
                          setIsMobileCushionFiltersOpen(false);
                        },
                      };
                    }),
                  ]}
                />

                <ExpandableModalOptions
                  isOpen={isMobileLegStyleFiltersOpen}
                  onClose={() => {
                    setIsMobileLegStyleFiltersOpen(false);
                  }}
                  label="Leg Style"
                  options={[
                    {
                      label: 'All',
                      id: 'all',
                      isSelected:
                        typeof state.filters.leg_style === 'undefined',
                      onClick: () => {
                        actions.clearFilterField('leg_style');
                        setIsMobileLegStyleFiltersOpen(false);
                      },
                    },
                    ...LEG_STYLE_OPTIONS.map(option => {
                      const isSelected =
                        state.filters.leg_style &&
                        state.filters.leg_style[option.value];

                      return {
                        label: option.label,
                        id: option.value,
                        isSelected,
                        onClick: () => {
                          actions.onFilterChange('leg_style', option.value);
                          setIsMobileLegStyleFiltersOpen(false);
                        },
                      };
                    }),
                  ]}
                />
              </MobileOnly>
            </div>
          </>
        )}
      </div>
    </>
  );
};

CollectionPage.propTypes = {
  actions: PropTypes.shape({
    clearFilterField: PropTypes.func,
    onFilterChange: PropTypes.func,
    addMainImages: PropTypes.func,
  }).isRequired,
  LUXContext: PropTypes.shape({
    isLUXInitSet: PropTypes.bool,
  }),
  state: PropTypes.shape({
    filters: PropTypes.shape({
      cushion_style: PropTypes.shape({}),
      leg_style: PropTypes.shape({}),
      style: PropTypes.shape({}),
    }),
    selectedCollection: PropTypes.shape({}),
  }).isRequired,
};

export default withState(CollectionPage);
